import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Megaphone, Pin, Calendar, Image as ImageIcon, Plus, Edit2, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getPromotions, addPromotion, updatePromotion, deletePromotion, uploadPromotionImage, getCommunityEvents, getContentLibraries, getPrograms, getAllContent, Timestamp } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { captureHandledException } from '@/lib/sentry';

interface Promotion {
  id: string;
  title: string;
  subText?: string;
  imgUrl?: string;
  imageUrl?: string; // Also handle imageUrl from Firestore
  pinned: boolean;
  startDate?: any;
  endDate?: any;
  createdAt?: any;
  status?: 'Expired' | 'Current' | 'Scheduled';
  route?: string;
  routeType?: 'Event' | 'Content Library' | 'Program' | 'Content Event' | 'Module' | 'Manual' | 'Content Video Series';
  routeParameter?: string;
  layout?: string;
  textColour?: string;
}

const promotionSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subText: z.string().optional(),
  imageUrl: z.string().optional(),
  pinned: z.boolean(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  route: z.string().optional(),
  routeType: z.enum(['Event', 'Content Library', 'Program', 'Content Event', 'Module', 'Manual', 'Content Video Series']).default('Manual'),
  routeParameter: z.string().optional(),
  textColour: z.enum(['light', 'dark']),
  layout: z.enum(['topLeft', 'topRight', 'bottomLeft', 'bottomRight']),
});

type PromotionFormData = z.infer<typeof promotionSchema>;

export default function Promotions() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [filteredPromotions, setFilteredPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedRouteType, setSelectedRouteType] = useState<string>('Manual');
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isContentEventModalOpen, setIsContentEventModalOpen] = useState(false);
  const [isContentLibraryModalOpen, setIsContentLibraryModalOpen] = useState(false);
  const [isProgramModalOpen, setIsProgramModalOpen] = useState(false);
  const [isVideoSeriesModalOpen, setIsVideoSeriesModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null); // Store selected event data
  const { user } = useAuth();
  const { toast } = useToast();

  const form = useForm<PromotionFormData>({
    resolver: zodResolver(promotionSchema),
    defaultValues: {
      title: '',
      subText: '',
      imageUrl: '',
      pinned: false,
      startDate: '',
      endDate: '',
      route: '',
      routeType: 'Manual' as const,
      routeParameter: '',
      textColour: 'dark' as const,
      layout: 'topLeft' as const,
    },
  });

  useEffect(() => {
    if (!user) return;

    logger.log('📢 Loading promotions...');

    const loadData = async () => {
      try {
        const promotionsData = await getPromotions() as Promotion[];
        logger.log('📋 Promotions loaded:', promotionsData.length);
        const promotionsWithStatus = promotionsData.map(promotion => ({
          ...promotion,
          status: calculateStatus(promotion)
        }));
        const sortedPromotions = promotionsWithStatus.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(0);
          return bDate.getTime() - aDate.getTime();
        });
        setPromotions(sortedPromotions);
      } catch (error: any) {
        logger.error('❌ Error loading promotions:', error);
        toast({ title: "Failed to load promotions", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };

    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user, toast]);

  // Filter promotions based on status
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredPromotions(promotions);
    } else {
      setFilteredPromotions(promotions.filter(promotion => promotion.status === statusFilter));
    }
  }, [promotions, statusFilter]);

  // Get unique status values for filter chips
  const statusCounts = promotions.reduce((acc, promotion) => {
    const status = promotion.status || 'Unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Clear selected event when route type changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'routeType') {
        setSelectedEvent(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const formatDate = (date: any): string => {
    if (!date) return 'ongoing';
    
    try {
      // Handle Firestore Timestamp
      if (date?.toDate) {
        return date.toDate().toLocaleDateString();
      }
      // Handle regular Date object or string
      return new Date(date).toLocaleDateString();
      } catch (error) {
        captureHandledException(error, "promotions.parse-date");
      return 'Invalid date';
    }
  };

  const calculateStatus = (promotion: Promotion): 'Expired' | 'Current' | 'Scheduled' => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    let startDate: Date | null = null;
    let endDate: Date | null = null;
    
    // Parse start date
    if (promotion.startDate) {
      if (promotion.startDate.toDate) {
        startDate = promotion.startDate.toDate();
      } else {
        startDate = new Date(promotion.startDate);
      }
      startDate?.setHours(0, 0, 0, 0);
    }
    
    // Parse end date
    if (promotion.endDate) {
      if (promotion.endDate.toDate) {
        endDate = promotion.endDate.toDate();
      } else {
        endDate = new Date(promotion.endDate);
      }
      endDate?.setHours(23, 59, 59, 999); // End of day
    }
    
    // Check if expired
    if (endDate && endDate < today) {
      return 'Expired';
    }
    
    // Check if scheduled
    if (startDate && startDate > today) {
      return 'Scheduled';
    }
    
    // Otherwise it's current (includes ongoing with null end dates)
    return 'Current';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Current':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  // Convert routeType to camelCase with spaces removed (for saving to Firestore)
  const formatRouteType = (routeType: string) => {
    return routeType
      .split(' ')
      .map((word, index) => index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  };

  // Convert camelCase routeType back to display format (for loading from Firestore)
  const parseRouteType = (routeType: string): 'Event' | 'Content Library' | 'Program' | 'Content Event' | 'Module' | 'Manual' | 'Content Video Series' => {
    switch (routeType?.toLowerCase()) {
      case 'event':
        return 'Event';
      case 'contentlibrary':
      case 'content library':
        return 'Content Library';
      case 'program':
        return 'Program';
      case 'contentevent':
      case 'content event':
        return 'Content Event';
      case 'module':
        return 'Module';
      case 'contentvideoseries':
      case 'content video series':
        return 'Content Video Series';
      case 'manual':
      default:
        return 'Manual';
    }
  };

  const handleSubmit = async (data: PromotionFormData) => {
    try {
      // Base promotion data that applies to both create and update
      const basePromotionData: any = {
        title: data.title,
        subText: data.subText || '',
        imageUrl: data.imageUrl || '',
        pinned: data.pinned,
        startDate: data.startDate ? Timestamp.fromDate(new Date(data.startDate)) : null,
        endDate: data.endDate ? Timestamp.fromDate(new Date(data.endDate)) : null,
        route: data.route || '',
        routeType: formatRouteType(data.routeType),
        routeParameter: data.routeParameter || '',
        layout: data.layout,
        textColour: data.textColour,
      };

      // Add event-specific fields if routeType is event or contentEvent and we have selected event data
      const formattedRouteType = formatRouteType(data.routeType);
      if ((formattedRouteType === 'event' || formattedRouteType === 'contentEvent') && selectedEvent) {
        basePromotionData.eventStartDate = selectedEvent.startDate || null;
        basePromotionData.eventEndDate = selectedEvent.endDate || null;
        basePromotionData.eventMonth = selectedEvent.month || '';
        basePromotionData.eventType = selectedEvent.type || '';
      }

      if (editingPromotion) {
        // Update existing promotion - don't include createdAt
        await updatePromotion(editingPromotion.id, basePromotionData);
        toast({
          title: "Promotion updated",
          description: "The promotion has been updated successfully.",
        });
        setIsEditModalOpen(false);
        setEditingPromotion(null);
      } else {
        // Create new promotion - include createdAt only for new promotions
        const newPromotionData = {
          ...basePromotionData,
          createdAt: Timestamp.now(),
        };
        await addPromotion(newPromotionData);
        toast({
          title: "Promotion created",
          description: "New promotion has been created successfully.",
        });
        setIsAddModalOpen(false);
      }

      form.reset({
        title: '',
        subText: '',
        imageUrl: '',
        pinned: false,
        startDate: '',
        endDate: '',
        route: '',
        routeType: 'Manual',
        routeParameter: '',
        textColour: 'dark',
        layout: 'topLeft',
      });

      // Clear selected event data
      setSelectedEvent(null);
    } catch (error) {
      logger.error('Error saving promotion:', error);
      toast({
        title: "Error",
        description: "Failed to save promotion. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    
    // Convert Firestore timestamps to date strings for the form
    const startDateStr = promotion.startDate?.toDate ? 
      promotion.startDate.toDate().toISOString().split('T')[0] : '';
    const endDateStr = promotion.endDate?.toDate ? 
      promotion.endDate.toDate().toISOString().split('T')[0] : '';

    // Parse the routeType from camelCase to display format
    const parsedRouteType = parseRouteType(promotion.routeType || '');

    form.reset({
      title: promotion.title,
      subText: promotion.subText || '',
      imageUrl: promotion.imageUrl || promotion.imgUrl || '',
      pinned: promotion.pinned,
      startDate: startDateStr,
      endDate: endDateStr,
      route: promotion.route || '',
      routeType: parsedRouteType,
      routeParameter: promotion.routeParameter || '',
      textColour: (promotion.textColour as 'light' | 'dark') || 'dark',
      layout: (promotion.layout as 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight') || 'topLeft',
    });
    
    setSelectedRouteType(parsedRouteType);
    
    setIsEditModalOpen(true);
  };

  const handleAdd = () => {
    form.reset({
      title: '',
      subText: '',
      imageUrl: '',
      pinned: false,
      startDate: '',
      endDate: '',
      route: '',
      routeType: 'Manual',
      routeParameter: '',
      textColour: 'dark',
      layout: 'topLeft',
    });
    setSelectedRouteType('Manual');
    setSelectedEvent(null); // Clear selected event data
    setIsAddModalOpen(true);
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    setUploadingImage(true);
    try {
      return await uploadPromotionImage(file);
    } catch (error) {
      logger.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    try {
      const downloadURL = await handleImageUpload(file);
      field.onChange(downloadURL);
      toast({
        title: "Image uploaded",
        description: "Image has been uploaded successfully.",
      });
    } catch (error) {
      captureHandledException(error, "promotions.upload-image");
      toast({
        title: "Upload failed",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (promotionId: string) => {
    try {
      await deletePromotion(promotionId);
      toast({
        title: "Promotion deleted",
        description: "The promotion has been deleted successfully.",
      });
    } catch (error) {
      logger.error('Error deleting promotion:', error);
      toast({
        title: "Error",
        description: "Failed to delete promotion. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading promotions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="h-6 w-6" />
              Promotions
            </h1>
            <p className="text-slate-600 mt-1">
              Manage promotional content and campaigns
            </p>
          </div>
          <Button onClick={handleAdd} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Promotion
          </Button>
        </div>

        {/* Status Filter Chips */}
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={statusFilter === 'all' ? 'default' : 'secondary'}
            className="cursor-pointer px-3 py-1"
            onClick={() => setStatusFilter('all')}
          >
            All ({promotions.length})
          </Badge>
          {['Current', 'Scheduled', 'Expired'].map((status) => (
            <Badge
              key={status}
              variant={statusFilter === status ? 'default' : 'secondary'}
              className={`cursor-pointer px-3 py-1 ${statusFilter === status ? '' : 'hover:bg-slate-200'}`}
              onClick={() => setStatusFilter(status)}
            >
              {status} ({statusCounts[status] || 0})
            </Badge>
          ))}
        </div>

        {/* Promotions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Promotions</CardTitle>
            <CardDescription>
              List of all promotional content in the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredPromotions.length === 0 ? (
              <div className="text-center py-12">
                <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {statusFilter === 'all' ? 'No promotions found' : `No ${statusFilter.toLowerCase()} promotions`}
                </h3>
                <p className="text-slate-600 mb-4">
                  {statusFilter === 'all' 
                    ? 'No promotional content has been added yet.' 
                    : `No promotions match the ${statusFilter.toLowerCase()} status filter.`}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Thumbnail</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-24 text-center">Pinned</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead className="w-48">Dates</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPromotions.map((promotion) => (
                      <TableRow key={promotion.id} data-testid={`row-promotion-${promotion.id}`}>
                        <TableCell>
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
                            {(promotion.imgUrl || promotion.imageUrl) ? (
                              <img
                                src={promotion.imgUrl || promotion.imageUrl}
                                alt={promotion.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.currentTarget as HTMLImageElement;
                                  target.style.display = 'none';
                                  const fallback = target.nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : (
                              <ImageIcon className="h-6 w-6 text-slate-400" />
                            )}
                            <div className="hidden w-full h-full items-center justify-center">
                              <ImageIcon className="h-6 w-6 text-slate-400" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-slate-900">
                              {promotion.title || 'Untitled Promotion'}
                            </div>
                            {promotion.subText && (
                              <div className="text-sm text-slate-600 mt-1">
                                {promotion.subText}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center">
                            {promotion.pinned ? (
                              <Pin className="h-4 w-4 text-amber-600 fill-amber-600" />
                            ) : (
                              <Pin className="h-4 w-4 text-slate-300" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(promotion.status!)}>
                            {promotion.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="h-3 w-3 text-slate-400" />
                              <span className="text-slate-600">Start:</span>
                              <span className="font-medium">
                                {formatDate(promotion.startDate)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="h-3 w-3 text-slate-400" />
                              <span className="text-slate-600">End:</span>
                              <span className="font-medium">
                                {formatDate(promotion.endDate)}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(promotion)}
                              className="h-8 w-8 p-0"
                              data-testid={`button-edit-promotion-${promotion.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`button-delete-promotion-${promotion.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Promotion</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{promotion.title}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(promotion.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Promotion Modal */}
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Promotion</DialogTitle>
              <DialogDescription>
                Create a new promotional content item for your platform.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {/* Text Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">Text Content</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter promotion title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subText"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subtitle</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Enter promotion subtitle" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="textColour"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Text Color</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select text color" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="light">Light</SelectItem>
                              <SelectItem value="dark">Dark</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="layout"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Layout</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select layout" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="topLeft">Top Left</SelectItem>
                              <SelectItem value="topRight">Top Right</SelectItem>
                              <SelectItem value="bottomLeft">Bottom Left</SelectItem>
                              <SelectItem value="bottomRight">Bottom Right</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Separator />

                {/* Image Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">Image</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Promotion Image</FormLabel>
                        <FormControl>
                          <div className="space-y-2">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange(e, field)}
                              disabled={uploadingImage}
                            />
                            {uploadingImage && (
                              <div className="text-sm text-slate-600">Uploading...</div>
                            )}
                            {field.value && (
                              <div className="mt-2">
                                <img
                                  src={field.value}
                                  alt="Preview"
                                  className="w-20 h-20 object-cover rounded border"
                                />
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Configuration Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">Configuration</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="routeType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Route Type</FormLabel>
                        <FormControl>
                          <Select 
                            value={field.value} 
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedRouteType(value);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select route type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Manual">Manual</SelectItem>
                              <SelectItem value="Event">Event</SelectItem>
                              <SelectItem value="Content Event">Content Event</SelectItem>
                              <SelectItem value="Content Library">Content Library</SelectItem>
                              <SelectItem value="Program">Program</SelectItem>
                              <SelectItem value="Content Video Series">Content Video Series</SelectItem>
                              <SelectItem value="Module">Module</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Conditional fields based on route type */}
                  {selectedRouteType === 'Manual' && (
                    <FormField
                      control={form.control}
                      name="route"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Route</FormLabel>
                          <FormControl>
                            <Input placeholder="/path" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {selectedRouteType === 'Module' && (
                    <FormField
                      control={form.control}
                      name="routeParameter"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Module</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select module" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Mind">Mind</SelectItem>
                                <SelectItem value="Body">Body</SelectItem>
                                <SelectItem value="Nutrition">Nutrition</SelectItem>
                                <SelectItem value="Sleep">Sleep</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {(selectedRouteType === 'Event' || selectedRouteType === 'Content Event' || selectedRouteType === 'Content Library' || selectedRouteType === 'Program' || selectedRouteType === 'Content Video Series') && (
                    <div className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name="routeParameter"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>Selected {selectedRouteType === 'Content Video Series' ? 'Video Series' : selectedRouteType}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={`No ${selectedRouteType === 'Content Video Series' ? 'video series' : selectedRouteType.toLowerCase()} selected`}
                                value={field.value}
                                readOnly
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (selectedRouteType === 'Event') setIsEventModalOpen(true);
                          else if (selectedRouteType === 'Content Event') setIsContentEventModalOpen(true);
                          else if (selectedRouteType === 'Content Library') setIsContentLibraryModalOpen(true);
                          else if (selectedRouteType === 'Program') setIsProgramModalOpen(true);
                          else if (selectedRouteType === 'Content Video Series') setIsVideoSeriesModalOpen(true);
                        }}
                        className="mt-8"
                      >
                        Select {selectedRouteType === 'Content Video Series' ? 'Video Series' : selectedRouteType}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>End Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="pinned"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Pinned</FormLabel>
                        <div className="text-sm text-muted-foreground">
                          Pin this promotion to highlight it
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    Create Promotion
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Edit Promotion Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Promotion</DialogTitle>
              <DialogDescription>
                Modify the promotional content settings and details.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {/* Text Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">Text Content</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter promotion title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subText"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subtitle</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Enter promotion subtitle" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="textColour"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Text Color</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select text color" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="light">Light</SelectItem>
                              <SelectItem value="dark">Dark</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="layout"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Layout</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select layout" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="topLeft">Top Left</SelectItem>
                              <SelectItem value="topRight">Top Right</SelectItem>
                              <SelectItem value="bottomLeft">Bottom Left</SelectItem>
                              <SelectItem value="bottomRight">Bottom Right</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Separator />

                {/* Image Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">Image</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Promotion Image</FormLabel>
                        <FormControl>
                          <div className="space-y-2">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange(e, field)}
                              disabled={uploadingImage}
                            />
                            {uploadingImage && (
                              <div className="text-sm text-slate-600">Uploading...</div>
                            )}
                            {field.value && (
                              <div className="mt-2">
                                <img
                                  src={field.value}
                                  alt="Preview"
                                  className="w-20 h-20 object-cover rounded border"
                                />
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Configuration Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">Configuration</h3>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="routeType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Route Type</FormLabel>
                        <FormControl>
                          <Select 
                            value={field.value} 
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedRouteType(value);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select route type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Manual">Manual</SelectItem>
                              <SelectItem value="Event">Event</SelectItem>
                              <SelectItem value="Content Event">Content Event</SelectItem>
                              <SelectItem value="Content Library">Content Library</SelectItem>
                              <SelectItem value="Program">Program</SelectItem>
                              <SelectItem value="Content Video Series">Content Video Series</SelectItem>
                              <SelectItem value="Module">Module</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Conditional fields based on route type */}
                  {selectedRouteType === 'Manual' && (
                    <FormField
                      control={form.control}
                      name="route"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Route</FormLabel>
                          <FormControl>
                            <Input placeholder="/path" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {selectedRouteType === 'Module' && (
                    <FormField
                      control={form.control}
                      name="routeParameter"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Module</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select module" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Mind">Mind</SelectItem>
                                <SelectItem value="Body">Body</SelectItem>
                                <SelectItem value="Nutrition">Nutrition</SelectItem>
                                <SelectItem value="Sleep">Sleep</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {(selectedRouteType === 'Event' || selectedRouteType === 'Content Event' || selectedRouteType === 'Content Library' || selectedRouteType === 'Program' || selectedRouteType === 'Content Video Series') && (
                    <div className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name="routeParameter"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>Selected {selectedRouteType === 'Content Video Series' ? 'Video Series' : selectedRouteType}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={`No ${selectedRouteType === 'Content Video Series' ? 'video series' : selectedRouteType.toLowerCase()} selected`}
                                value={field.value}
                                readOnly
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (selectedRouteType === 'Event') setIsEventModalOpen(true);
                          else if (selectedRouteType === 'Content Event') setIsContentEventModalOpen(true);
                          else if (selectedRouteType === 'Content Library') setIsContentLibraryModalOpen(true);
                          else if (selectedRouteType === 'Program') setIsProgramModalOpen(true);
                          else if (selectedRouteType === 'Content Video Series') setIsVideoSeriesModalOpen(true);
                        }}
                        className="mt-8"
                      >
                        Select {selectedRouteType === 'Content Video Series' ? 'Video Series' : selectedRouteType}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>End Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="pinned"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Pinned</FormLabel>
                        <div className="text-sm text-muted-foreground">
                          Pin this promotion to highlight it
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingPromotion(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    Update Promotion
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Event Selection Modal */}
        <Dialog open={isEventModalOpen} onOpenChange={setIsEventModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Select Event</DialogTitle>
              <DialogDescription>
                Choose an event to link to this promotion. Only community events (not content events) are shown.
              </DialogDescription>
            </DialogHeader>
            <EventSelectionContent 
              onSelect={(event) => {
                form.setValue('routeParameter', event.id);
                setSelectedEvent(event); // Store the full event object
                setIsEventModalOpen(false);
                toast({
                  title: "Event selected",
                  description: `Selected: ${event.title}`,
                });
              }}
              onCancel={() => setIsEventModalOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Content Event Selection Modal */}
        <Dialog open={isContentEventModalOpen} onOpenChange={setIsContentEventModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Select Content Event</DialogTitle>
              <DialogDescription>
                Choose a content event to link to this promotion. Only content events are shown.
              </DialogDescription>
            </DialogHeader>
            <ContentEventSelectionContent 
              onSelect={(event) => {
                form.setValue('routeParameter', event.id);
                setSelectedEvent(event); // Store the full event object
                setIsContentEventModalOpen(false);
                toast({
                  title: "Content event selected",
                  description: `Selected: ${event.title}`,
                });
              }}
              onCancel={() => setIsContentEventModalOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Content Library Selection Modal */}
        <Dialog open={isContentLibraryModalOpen} onOpenChange={setIsContentLibraryModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Select Content Library</DialogTitle>
              <DialogDescription>
                Choose a content library item to link to this promotion.
              </DialogDescription>
            </DialogHeader>
            <ContentLibrarySelectionContent 
              onSelect={(item) => {
                form.setValue('routeParameter', item.id);
                setIsContentLibraryModalOpen(false);
                toast({
                  title: "Content library selected",
                  description: `Selected: ${item.title || item.name}`,
                });
              }}
              onCancel={() => setIsContentLibraryModalOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Program Selection Modal */}
        <Dialog open={isProgramModalOpen} onOpenChange={setIsProgramModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Select Program</DialogTitle>
              <DialogDescription>
                Choose an enabled program to link to this promotion.
              </DialogDescription>
            </DialogHeader>
            <ProgramSelectionContent 
              onSelect={(program) => {
                form.setValue('routeParameter', program.id);
                setIsProgramModalOpen(false);
                toast({
                  title: "Program selected",
                  description: `Selected: ${program.title || program.name}`,
                });
              }}
              onCancel={() => setIsProgramModalOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Video Series Selection Modal */}
        <Dialog open={isVideoSeriesModalOpen} onOpenChange={setIsVideoSeriesModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Select Video Series</DialogTitle>
              <DialogDescription>
                Choose a video series to link to this promotion.
              </DialogDescription>
            </DialogHeader>
            <VideoSeriesSelectionContent 
              onSelect={(series) => {
                form.setValue('routeParameter', series.id);
                setIsVideoSeriesModalOpen(false);
                toast({
                  title: "Video series selected",
                  description: `Selected: ${series.title}`,
                });
              }}
              onCancel={() => setIsVideoSeriesModalOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Event Selection Component
interface EventSelectionContentProps {
  onSelect: (event: any) => void;
  onCancel: () => void;
}

function EventSelectionContent({ onSelect, onCancel }: EventSelectionContentProps) {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const all = await getCommunityEvents() as any[];
        setEvents(all.filter((event: any) => event.type !== 'content'));
      } catch (error) {
        logger.error('Error loading events:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  const filteredEvents = events.filter((event: any) =>
    event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-8">Loading events...</div>;
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search events..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      
      <div className="max-h-96 overflow-y-auto space-y-2">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            No events found
          </div>
        ) : (
          filteredEvents.map((event: any) => (
            <div
              key={event.id}
              className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
              onClick={() => onSelect(event)}
            >
              <h4 className="font-medium">{event.title}</h4>
              {event.description && (
                <p className="text-sm text-slate-600 mt-1">{event.description}</p>
              )}
              <div className="text-xs text-slate-500 mt-2">
                Type: {event.type} | Start: {event.startDate?.toDate?.()?.toLocaleDateString() || 'N/A'}
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Content Event Selection Component
interface ContentEventSelectionContentProps {
  onSelect: (event: any) => void;
  onCancel: () => void;
}

function ContentEventSelectionContent({ onSelect, onCancel }: ContentEventSelectionContentProps) {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const all = await getCommunityEvents() as any[];
        setEvents(all.filter((event: any) => event.type === 'content'));
      } catch (error) {
        logger.error('Error loading content events:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  const filteredEvents = events.filter((event: any) =>
    event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-8">Loading content events...</div>;
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search content events..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      
      <div className="max-h-96 overflow-y-auto space-y-2">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            No content events found
          </div>
        ) : (
          filteredEvents.map((event: any) => (
            <div
              key={event.id}
              className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
              onClick={() => onSelect(event)}
            >
              <h4 className="font-medium">{event.title}</h4>
              {event.description && (
                <p className="text-sm text-slate-600 mt-1">{event.description}</p>
              )}
              <div className="text-xs text-slate-500 mt-2">
                Type: {event.type} | Start: {event.startDate?.toDate?.()?.toLocaleDateString() || 'N/A'}
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Content Library Selection Component
interface ContentLibrarySelectionContentProps {
  onSelect: (item: any) => void;
  onCancel: () => void;
}

function ContentLibrarySelectionContent({ onSelect, onCancel }: ContentLibrarySelectionContentProps) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadItems = async () => {
      try {
        setItems(await getContentLibraries() as any[]);
      } catch (error) {
        logger.error('Error loading content library:', error);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, []);

  const filteredItems = items.filter((item: any) =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-8">Loading content library...</div>;
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search content library..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      
      <div className="max-h-96 overflow-y-auto space-y-2">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            No content library items found
          </div>
        ) : (
          filteredItems.map((item: any) => (
            <div
              key={item.id}
              className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
              onClick={() => onSelect(item)}
            >
              <h4 className="font-medium">{item.title || item.name}</h4>
              {item.description && (
                <p className="text-sm text-slate-600 mt-1">{item.description}</p>
              )}
              <div className="text-xs text-slate-500 mt-2">
                {item.mediaType && `Type: ${item.mediaType}`}
                {item.module && ` | Module: ${item.module}`}
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Program Selection Component
interface ProgramSelectionContentProps {
  onSelect: (program: any) => void;
  onCancel: () => void;
}

function ProgramSelectionContent({ onSelect, onCancel }: ProgramSelectionContentProps) {
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const all = await getPrograms() as any[];
        setPrograms(all.filter((p: any) => p.enabled === true));
      } catch (error) {
        logger.error('Error loading programs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPrograms();
  }, []);

  const filteredPrograms = programs.filter((program: any) =>
    program.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    program.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    program.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-8">Loading programs...</div>;
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search programs..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      
      <div className="max-h-96 overflow-y-auto space-y-2">
        {filteredPrograms.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            No enabled programs found
          </div>
        ) : (
          filteredPrograms.map((program: any) => (
            <div
              key={program.id}
              className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
              onClick={() => onSelect(program)}
            >
              <h4 className="font-medium">{program.title || program.name}</h4>
              {program.description && (
                <p className="text-sm text-slate-600 mt-1">{program.description}</p>
              )}
              <div className="text-xs text-slate-500 mt-2">
                {program.module && `Module: ${program.module}`}
                {program.duration && ` | Duration: ${program.duration}`}
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Video Series Selection Component
interface VideoSeriesSelectionContentProps {
  onSelect: (series: any) => void;
  onCancel: () => void;
}

function VideoSeriesSelectionContent({ onSelect, onCancel }: VideoSeriesSelectionContentProps) {
  const [videoSeries, setVideoSeries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadVideoSeries = async () => {
      try {
        const all = await getAllContent() as any[];
        setVideoSeries(all.filter((c: any) => c.mediaType === 'videoSeries'));
      } catch (error) {
        logger.error('Error loading video series:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVideoSeries();
  }, []);

  const filteredSeries = videoSeries.filter((series: any) =>
    series.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    series.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-8">Loading video series...</div>;
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search video series..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      
      <div className="max-h-96 overflow-y-auto space-y-2">
        {filteredSeries.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            No video series found
          </div>
        ) : (
          filteredSeries.map((series: any) => (
            <div
              key={series.id}
              className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer flex items-center gap-4"
              onClick={() => onSelect(series)}
            >
              {series.thumbnailUrl && (
                <img
                  src={series.thumbnailUrl}
                  alt={series.title}
                  className="w-20 h-12 object-cover rounded"
                />
              )}
              <div className="flex-1">
                <h4 className="font-medium">{series.title}</h4>
                {series.description && (
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{series.description}</p>
                )}
                <div className="text-xs text-slate-500 mt-2">
                  {series.episodes && `${Object.keys(series.episodes).length} episodes`}
                  {series.contentLibraries?.length > 0 && ` | Libraries: ${series.contentLibraries.join(', ')}`}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}